import type { IconProps } from '/#/components/base/icon/icon'

import isFunction from 'lodash-es/isFunction'
import isString from 'lodash-es/isString'
import isUndefined from 'lodash-es/isUndefined'
import merge from 'lodash-es/merge'

import { isEmpty } from '/@/utils/is'

/**
 * @method mergeIconProps
 * @description Merges default icon options with user-provided icon props, handling cases where the icon is a string or an object.
 * @param {Partial<IconProps>} defaultIconOptions - The default icon options to be applied if the user provides an icon string.
 * @param {IconProps | string | undefined} icon - The user-provided icon, which can be an `IconProps` object, a string representing the icon name, or `undefined`.
 * @returns {IconProps | undefined} The merged icon props, or `undefined` if the provided `icon` is `undefined`.
 * @example
 * // Returns { name: 'home', color: 'blue' }
 * mergeIconProps({ color: 'blue', name: 'home'})
 *
 * // Returns { name: 'settings', size: 'lg' }
 * mergeIconProps({ color: 'blue'   name: 'settings', size: 'lg' })
 */
export function mergeIconProps(
  defaultIconOptions: Partial<IconProps>,
  icon?: IconProps | string,
): IconProps | undefined {
  if (isUndefined(icon)) return undefined

  if (isString(icon)) {
    return merge(defaultIconOptions, { name: icon }) as IconProps
  }

  return merge(defaultIconOptions, icon)
}

/**
 * @method mergeProps
 * @description Merges default options with user-provided props, handling cases where the prop is a string or an object.
 * @param {Partial<T>} defaultOptions - The default options to be applied.
 * @param {T | string | undefined} userProp - The user-provided prop, which can be an object, a string, or undefined.
 * @param {string} [stringPropKey='name'] - The key to use when userProp is a string.
 * @returns {T | undefined} The merged props, or undefined if userProp is undefined.
 * @example
 * // For icons: Returns { name: 'home', color: 'blue' }
 * mergeProps<IconProps>({ color: 'blue' }, 'home')
 *
 * // For other components: Returns { label: 'Submit', size: 'lg' }
 * mergeProps<ButtonProps>({ size: 'lg' }, 'Submit', 'label')
 */
export function mergeProps<T extends Record<string, any>>(
  defaultOptions: Partial<T>,
  userProp: T | string | undefined,
  stringPropKey: string = 'name',
): T | undefined {
  if (isUndefined(userProp)) return undefined

  if (isString(userProp)) {
    return merge({} as T, defaultOptions, { [stringPropKey]: userProp }) as T
  }

  return merge({} as T, defaultOptions, userProp)
}

/**
 * Evaluates a property that could be a function or a direct value
 * @param prop - The property to evaluate (function or value)
 * @param context - The context to pass to the function if prop is a function
 * @param defaultValue - The default value to return if prop is empty
 * @returns The evaluated result
 */
export function evaluateProperty<T, C = any>(
  prop: ((...args: any[]) => T) | T | undefined,
  context?: C,
  defaultValue: T = false as unknown as T,
): T {
  if (isFunction(prop)) return (prop as Function)(context)
  if (isEmpty(prop)) return defaultValue
  return prop as T
}
